/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useHistory } from 'react-router-dom';
import { Fragment, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import Table, { TableProps } from 'antd/lib/table';
// local imports
import { useStyles } from '~src/hooks/useStyles';
import { useFiles } from '~src/hooks/api-hooks/useFiles';
import { useProjectId } from '~src/hooks/useProjectId';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import {
  dbFileToTableEntry,
  dbFolderToTableEntry,
  sortByModified,
  sortByType,
  TableEntry,
} from './FileTable.utils';
import IconButton from '~components/_common/IconButton/IconButton';
import { VersionSelect } from '~components/_common/VersionSelect/VersionSelect';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { styleFn } from './FileTable.style';
import { FileTableProps } from './FileTable';
import { SearchContext } from '~src/contexts/SearchContext';
import { filterByQuery } from '~src/utils/searchFilters';
import { uniq } from 'lodash';
import { queryCache, useMutation } from 'react-query';
import { toggleFolderNotify, toggleVFileNotify } from '~src/services/api';
import { message, Popover, Tooltip, Tag as TagAntd } from 'antd';
import { DbFile, DbFolder } from '~src/types/db';
import { useFileViewerContext } from '~src/contexts/FileToViewContext';
import { useSortByRevision } from '~src/hooks/utils/useSortByRevision';
import { formatDate } from '~src/utils/formatDate';
import _ from 'lodash';
import { DB_STATE } from '~src/types/enums';
import { getTagsForFile } from '~src/utils/tags';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { ColumnType } from 'antd/es/table/interface';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { getTenderIconArichive } from '~components/molecules/LeftSideView/utils';
import { SVFIconType } from '~components/atoms/Viewers/SVFViewer/components/SVFIconType';

/** Favourite files/folders table */
export const FavouritesTable: React.FC<FileTableProps> = ({
  selectedKeys,
  onSelect,
  highlightedRow,
  handleRow,
  setSelectedItems,
  focusOptions,
  openSVFTranscribe,
  ...tableProps
}) => {
  const filesQueryBaseKeys = {
    withVersions: true,
    favourites: true,
    oldFilesImplementation: true,
  };
  const foldersQueryBaseKeys = {
    favourites: true,
    as_admin:
      window.sessionStorage.getItem('appState') === 'superUser'
        ? true
        : undefined,
  };

  const styles = useStyles(styleFn);
  const intl = useIntl();
  const projectId = useProjectId();
  const { setFileToView } = useFileViewerContext();
  const history = useHistory();
  const { fileVersionsContext } = useContext(FileVersionsContext);
  const archiveSettings = useArchiveSettings();
  const isTagsOn = archiveSettings?.data?.resArchiveSettings?.is_tags_on;
  const tagsData = archiveSettings.data?.resTags;
  const filesQuery = useFiles(filesQueryBaseKeys, {
    onError: () =>
      // go to /archive
      history.replace(`/projects/${projectId}/archive`),
  });
  const { sortByRevision } = useSortByRevision();

  const [
    doUpdateFileNotifications,
    updateFileNotificationMutation,
  ] = useMutation(toggleVFileNotify, {
    onError: () => {
      message.error(intl.formatMessage({ id: 'notification-toggle-error' }));
    },
    onSuccess: ({ is_notify }, { versionedFileId }) => {
      queryCache.setQueryData<DbFile[]>(
        ['files', { ...filesQueryBaseKeys, projectId: String(projectId) }],
        (prevFiles = []) => {
          const changedFileIndex = prevFiles.findIndex(
            (item) => item.id === versionedFileId
          );
          if (changedFileIndex >= 0) {
            const newList = [...prevFiles];
            newList[changedFileIndex] = {
              ...newList[changedFileIndex],
              is_notify,
            };
            return newList;
          }
          return prevFiles;
        }
      );
    },
  });
  const foldersQuery = useFolders(foldersQueryBaseKeys);

  const [
    doUpdateFolderNotifications,
    updateFolderNotificationMutation,
  ] = useMutation(toggleFolderNotify, {
    onError: () => {
      message.error(intl.formatMessage({ id: 'notification-toggle-error' }));
    },
    onSuccess: ({ is_notify }, { folderId }) => {
      queryCache.setQueryData<DbFolder[]>(
        ['folders', { ...foldersQueryBaseKeys, projectId: String(projectId) }],
        (prevFolders = []) => {
          const changedFolderIndex = prevFolders.findIndex(
            (item) => item.id === folderId
          );
          if (changedFolderIndex >= 0) {
            const newList = [...prevFolders];
            newList[changedFolderIndex] = {
              ...newList[changedFolderIndex],
              is_notify,
            };
            return newList;
          }
          return prevFolders;
        }
      );
    },
  });

  const foldersSource = useMemo(
    () => (foldersQuery.data || []).map(dbFolderToTableEntry),
    [foldersQuery.data]
  );
  const filesSource = useMemo(
    () =>
      (filesQuery.data || []).map((item) => dbFileToTableEntry(item)).reverse(),
    [filesQuery.data]
  );

  const searchContext = useContext(SearchContext);

  const filteredFolders = [
    ...foldersSource.filter((item) => {
      if (
        filterByQuery(item.name, searchContext.query) ||
        filterByQuery(item.description, searchContext.query) ||
        filterByQuery(item.type.icon, searchContext.query) ||
        filterByQuery(
          item.dbFile?.versions &&
            item.dbFile?.versions[0] &&
            item.dbFile?.versions[0].version,
          searchContext.query
        )
      ) {
        return item;
      }
      return null;
    }),
  ];

  const filteredFiles = [
    ...filesSource.filter((item) => {
      //getting index of version in [versions] array by checking fileVersionsContext and [all current versions] array
      //Array.prototype.findIndex() || gets the index of the first element in the array that satisfies the provided testing function
      const dynamicIndex = (item.dbFile?.versions || []).findIndex(
        (el) =>
          //provided testing function
          el.version === fileVersionsContext[item.id]?.version &&
          fileVersionsContext[item.id]?.version
      );
      if (
        filterByQuery(item.name, searchContext.query) ||
        filterByQuery(item.description, searchContext.query) ||
        filterByQuery(item.type.icon, searchContext.query) ||
        filterByQuery(
          dynamicIndex > 0
            ? item.dbFile?.versions &&
                item.dbFile?.versions[dynamicIndex] &&
                item.dbFile?.versions[dynamicIndex].version
            : item.dbFile?.versions &&
                item.dbFile?.versions[0] &&
                item.dbFile?.versions[0].version,
          searchContext.query
        )
      ) {
        return item;
      }
      return null;
    }),
  ];

  const handleSelectShift = (record: TableEntry, index?: number) => {
    if (selectedKeys && selectedKeys.length === 0) {
      return setSelectedItems((prevSelectedItems) => [
        record,
        ...prevSelectedItems,
      ]);
    }
    if (selectedKeys && selectedKeys.length > 0) {
      const selectedIndex = selectedKeys.findIndex(
        (element) => element === record.key
      );
      if (selectedIndex >= 0) {
        return setSelectedItems((prevItems) =>
          prevItems.filter((item) => item.key !== record.key)
        );
      }
      const tableKeys = getTableData().map((item) => item.key);

      const isAnySelectedInCurrentView = selectedKeys.some((el) =>
        tableKeys.includes(el as string)
      );
      if (!isAnySelectedInCurrentView) {
        return setSelectedItems((prevSelectedItems) => [
          record,
          ...prevSelectedItems,
        ]);
      }

      const lastIndexReversed = getTableData()
        .reverse()
        .findIndex((el) => selectedKeys.includes(el.key));

      const lastIndex = getTableData().findIndex(
        (el) => el.key === getTableData().reverse()[lastIndexReversed].key
      );
      const firstIndex = getTableData().findIndex((el) =>
        selectedKeys.includes(el.key)
      );

      if ((index as number) >= 0) {
        if ((index as number) < firstIndex) {
          return setSelectedItems((prevState) => [
            ...prevState,
            ...getTableData().slice(index, firstIndex),
          ]);
        }

        if ((index as number) > lastIndex) {
          return setSelectedItems((prevState) => [
            ...prevState,
            ...getTableData().slice(lastIndex + 1, (index as number) + 1),
          ]);
        }

        if ((index as number) > firstIndex && (index as number) < lastIndex) {
          return setSelectedItems((prevState) =>
            uniq([
              ...prevState,
              ...getTableData().slice(firstIndex, (index as number) + 1),
            ])
          );
        }
      }
    }
  };

  const getBackgroundRow = (record: TableEntry) => {
    const data =
      record.dbFolder && record.dbFolder.focus
        ? _.find(focusOptions, {
            value: record.dbFolder.focus,
          })
        : '';
    if (
      (record.key === highlightedRow?.key && data) ||
      record.disabled ||
      window.sessionStorage.getItem('appState') === 'timeMachine'
    )
      return {
        color: 'black',
        background: `${data ? '#' + data.color_hex_code_opacity : ''}`,
      };
    return {
      color: 'black',
      background: `${data ? '#' + data.color_hex_code : ''}`,
    };
  };

  const columns = useMemo<TableProps<TableEntry>['columns']>(
    () => [
      {
        title: '',
        width: 30,
        align: 'center',
        dataIndex: 'type',
        render: (type, record) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: (
              <a
                className="ant-dropdown-link"
                onClick={(e) => {
                  e.preventDefault();
                  const customEvent = new MouseEvent('contextmenu', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    button: 2,
                    buttons: 2,
                    clientX: e.clientX,
                    clientY: e.clientY,
                  });
                  e.currentTarget.dispatchEvent(customEvent);
                }}
                css={styles.dotAnchor}>
                <IconButton
                  iconStyle="fal"
                  icon="ellipsis-v"
                  customStyle={styles.dotIcon}
                  color={'darkGray'}
                  tooltip={intl.formatMessage({ id: 'actions' })}
                />
              </a>
            ),
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'type' }),
        dataIndex: 'type',
        width: 60,
        align: 'center',
        render: (type, record) => {
          const isTenderFolder = record.dbFolder?.option?.is_tender_folder;
          const isPreviewableFormat =
            type.icon === 'file-pdf' || type.icon === 'file-image';

          return {
            children: (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {isTenderFolder ? (
                    <NavIcon
                      customStyle={{ marginLeft: '32px' }}
                      icon={getTenderIconArichive(record.dbFolder?.option)}
                    />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {/* 1) Eye PDF/IMG */}
                      {isPreviewableFormat && (
                        <IconButton
                          click={() => {
                            if (record.dbFile?.versions?.length) {
                              setFileToView({ ...record.dbFile });
                            } else {
                              message.error(
                                intl.formatMessage({ id: 'file-preview-error' })
                              );
                            }
                          }}
                          iconStyle={type.iconStyle}
                          icon={['far', 'eye']}
                          color="red"
                        />
                      )}

                      {/* 2) SVF: transcribe/view */}
                      {(() => {
                        const version = fileVersionsContext[record.id];
                        const hasModel = version?.extension_svf_model != null;
                        const isConvertible =
                          version?.is_svf_convertible === true;
                        const isViewable =
                          version?.extension_svf_model?.state === 7;
                        const shouldShow = isConvertible || isViewable;
                        if (!shouldShow) return null;

                        return (
                          <SVFIconType
                            key={`svf-${record.id}-${
                              version?.extension_svf_model?.state ?? 'none'
                            }`}
                            version={version}
                            extension={record.extension}
                            variant="sidebar"
                            disabled={false}
                            customTooltip={(() => {
                              const state = version?.extension_svf_model?.state;
                              if (!hasModel && isConvertible)
                                return intl.formatMessage({
                                  id: 'svf-view-file-convert',
                                });
                              if ([1, 2, 4].includes(state as number))
                                return intl.formatMessage({
                                  id: 'svf-view-file-progress',
                                });
                              if (state === 7)
                                return intl.formatMessage({
                                  id: 'svf-view-file-window',
                                });
                              if (state === 5)
                                return intl.formatMessage({
                                  id: 'svf-view-file-convert',
                                });
                              return intl.formatMessage({
                                id: 'svf-view-file',
                              });
                            })()}
                            onClick={() => {
                              if (!record.dbFile) {
                                message.error(
                                  intl.formatMessage({
                                    id: 'file-preview-error',
                                  })
                                );
                                return;
                              }
                              const v = fileVersionsContext[record.id];
                              const ready = v?.extension_svf_model?.state === 7;

                              if (ready) {
                                setFileToView({ ...record.dbFile });
                                return;
                              }
                              if (
                                openSVFTranscribe &&
                                (isConvertible ||
                                  v?.extension_svf_model?.state === 5)
                              ) {
                                openSVFTranscribe({ ...record.dbFile });
                                setFileToView(undefined);
                              }
                            }}
                            type={type}
                          />
                        );
                      })()}

                      {/* 3) File/CAD */}
                      {(() => {
                        const ext = record.extension?.toLowerCase() || '';
                        const isCadFile = ['rvt', 'dwg', 'ifc'].includes(ext);

                        if (isCadFile) {
                          return (
                            <SVFIconType
                              key={`file-icon-${record.id}`}
                              version={fileVersionsContext[record.id]}
                              extension={record.extension}
                              variant="table"
                              customTooltip={intl.formatMessage(
                                { id: type.icon },
                                { type: record.extension?.toUpperCase() }
                              )}
                              onClick={() => {
                                // optional
                              }}
                              type={type}
                            />
                          );
                        }

                        return (
                          <IconButton
                            iconStyle={type.iconStyle}
                            icon={type.icon}
                            iconSecond={
                              record.state === DB_STATE.REMOVED ||
                              record.state === DB_STATE.DELETED
                                ? ['fad', 'trash']
                                : null
                            }
                            colorSecondIcon="red"
                            customStyleSecondIcon={{
                              left: '12px',
                              top: '10px',
                            }}
                            color={type.color}
                            customStyle={styles.icons}
                            tooltip={intl.formatMessage(
                              { id: type.icon },
                              { type: record.extension?.toUpperCase() }
                            )}
                            badge={
                              record.state !== DB_STATE.REMOVED &&
                              record.state !== DB_STATE.DELETED &&
                              type.is_empty &&
                              '0'
                            }
                            backgroundOff
                          />
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            ),
          };
        },
        sorter: sortByType,
      },
      {
        title: intl.formatMessage({ id: 'name' }),
        dataIndex: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (node, record) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: node,
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'description' }),
        dataIndex: 'description',
        sorter: (a, b) => {
          if (!a.description && !b.description) return 0;
          if (!a.description) return -1;
          if (!b.description) return 1;
          return (a.description || '').localeCompare(b.description || '');
        },
        width: 200,
        ellipsis: true,
        render: (node, record) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: node,
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'revision' }),
        dataIndex: 'dbFile',
        sorter: (a, b) => sortByRevision(a, b),
        width: 200,
        ellipsis: true,
        render: (x, record) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children:
              record.dbFile &&
              record.dbFile.versions &&
              record.dbFile.versions[0]?.version,
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'tags' }),
        dataIndex: 'tags',
        width: 215,
        render: (tag, record) => {
          const tagsArray = record?.dbFile?.tags || [];
          const tagsDataActive = tagsData?.filter(
            (tag) => tag.state !== DB_STATE.REMOVED
          );
          const tags = getTagsForFile(tagsArray, tagsDataActive);

          if (!record.dbFile || !tags) {
            return null;
          }

          const maxVisibleTags = 2;
          const hiddenCount =
            tags.length > maxVisibleTags ? tags.length - maxVisibleTags : 0;

          return {
            children: (
              <Fragment>
                {tags.slice(0, maxVisibleTags).map((tagItem, index) => {
                  const tag = String(tagItem);
                  return (
                    <TagAntd key={index} color={'#1f5fa6'}>
                      <Tooltip title={tag}>
                        {tag.length > 8 ? `${tag.substring(0, 8)}...` : tag}
                      </Tooltip>
                    </TagAntd>
                  );
                })}
                {hiddenCount > 0 && (
                  <Popover
                    overlayStyle={{ minWidth: 100, maxWidth: 250 }}
                    content={
                      <div
                        css={{
                          padding: '10px',
                        }}>
                        <h3>
                          <b>{intl.formatMessage({ id: 'tags' })}</b>
                        </h3>
                        {tags.map((tag, index) => (
                          <TagAntd
                            style={{ marginBottom: '4px' }}
                            key={index}
                            color={'#1f5fa6'}>
                            {tag}
                          </TagAntd>
                        ))}
                      </div>
                    }>
                    <TagAntd>+{hiddenCount}</TagAntd>
                  </Popover>
                )}
              </Fragment>
            ),
          };
        },
      },
      {
        title: (
          <IconButton
            containerStyle={{ minHeight: '18px' }}
            icon={['far', 'envelope']}
            backgroundOff
          />
        ),
        dataIndex: 'notifications',
        width: 60,
        align: 'center',
        render: (text, element, index) => {
          return {
            props: {
              style: getBackgroundRow(element),
            },
            children: (
              <IconButton
                icon={
                  element.notifications
                    ? ['fad', 'check-circle']
                    : ['far', 'circle']
                }
                color={element.notifications ? 'green' : 'darkGray'}
                customStyle={[styles.icons, styles.small]}
                backgroundOff
                click={async () => {
                  if (
                    window.sessionStorage.getItem('appState') !== 'timeMachine'
                  )
                    element.folder
                      ? doUpdateFolderNotifications({
                          projectId: Number(projectId),
                          folderId: element.id,
                          notify: !element.notifications,
                          recursive: false,
                        })
                      : doUpdateFileNotifications({
                          projectId: Number(projectId),
                          versionedFileId: element.id,
                          notify: !element.notifications,
                        });
                }}
              />
            ),
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'ver' }),
        dataIndex: 'dbFile',
        width: 80,
        align: 'center',
        onCell: (element) => ({
          style: { padding: '4px 10px' },
        }),
        render: (file, element) => {
          return {
            props: {
              style: getBackgroundRow(element),
            },
            children: file && <VersionSelect file={file} />,
          };
        },
        /*         shouldCellUpdate: (record, prevRecord) =>
          record.dbFile?.versions !== prevRecord.dbFile?.versions, */
      },
      {
        title: intl.formatMessage({ id: 'modified-date' }),
        dataIndex: 'updatedAt',
        sorter: sortByModified(fileVersionsContext),
        width: 140,
        align: 'center',
        render: (updatedAt, record) => {
          let modified = updatedAt;

          // if file, show "modified" of selected version
          if (record.dbFile?.versions) {
            modified =
              fileVersionsContext[record.id]?.updated_at ||
              record.dbFile.versions[0]?.updated_at;
          }
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: formatDate(modified, 'short'),
          };
        },
      },
    ],
    [
      fileVersionsContext,
      intl,
      styles.icons,
      styles.small,
      focusOptions,
      highlightedRow,
      tagsData,
    ]
  );

  const getTableData = () => {
    return [...filteredFolders, ...filteredFiles];
  };

  const filteredColumns = (columns as ColumnType<TableEntry>[]).filter(
    (column) => {
      return column?.dataIndex !== 'tags';
    }
  );

  const updatedColumns = !isTagsOn ? filteredColumns : columns;

  return (
    <Table<TableEntry>
      showSorterTooltip={false}
      css={[
        styles.table,
        tableProps.scroll?.y && {
          '.ant-table-body': {
            height: tableProps.scroll.y,
          },
        },
      ]}
      className="main-content-window"
      pagination={false}
      bordered
      columns={updatedColumns}
      dataSource={[...filteredFolders, ...filteredFiles]}
      loading={
        filesQuery.isLoading ||
        filesQuery.isIdle ||
        foldersQuery.isLoading ||
        foldersQuery.isIdle
      }
      rowClassName={(record) => {
        let className = '';
        if (record.key === highlightedRow?.key) {
          className += 'ant-table-row-selected' + ' ';
        }
        if (record.disabled) {
          className += 'unfinished' + ' ';
        }

        return className;
      }}
      rowSelection={{
        type: 'checkbox',
        selectedRowKeys: selectedKeys,
        columnWidth: 50,
        hideSelectAll: true,
        renderCell: (type, record, element, node) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: node,
          };
        },
        onSelect,
        getCheckboxProps: (record) => ({
          disabled: record.disabled,
        }),
      }}
      onRow={(record, index) => ({
        ...handleRow(record, index),
        onClick: (event) => {
          if (event.shiftKey) {
            return handleSelectShift(record, index);
          }
          const onClickAction = handleRow(record, index).onClick;
          if (onClickAction) {
            return onClickAction(event);
          }
        },
      })}
      {...tableProps}
    />
  );
};
